<template>
  <v-dialog v-model="subgroupDialog" max-width="680" @click:outside="closeDialog">
    <template v-slot:activator="{ on, attrs }">
      <v-btn v-if="!noActivator" class="primary" dark v-bind="attrs" v-on="on">{{
        $t("common.create")
      }}</v-btn>
    </template>
    <v-form v-model="formValid" ref="form">
      <v-card>
        <v-card-title class="headline primary white--text"
          >{{ $t(`common.${subgroupkey ? "edit" : "new"}`) }} {{ subgroupName }}</v-card-title
        >
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="9">
                <v-text-field
                  v-model="subgroup.name"
                  :label="this.$t('common.name')"
                  required
                  this.dialogLoading="true"
                  :rules="[rules.required, rules.nameNotTaken]"
                ></v-text-field>
                <v-text-field
                  v-model="subgroup.description"
                  :label="this.$t('common.description')"
                  required
                  :rules="[rules.required]"
                ></v-text-field>
                <v-autocomplete
                  v-model="autocomplete"
                  :items="allUsers"
                  item-text="name"
                  item-value="id"
                  :label="this.$t('common.travelers')"
                  single-line
                  @change="selectedUser"
                ></v-autocomplete>
                <div class="d-flex flex-wrap">
                  <v-chip
                    v-for="userId in users"
                    class="ma-1"
                    close
                    @click:close="removeUser(userId)"
                    :key="userId"
                  >
                    {{ userName(userId) }}
                  </v-chip>
                </div>
                <v-checkbox
                  :disabled="subgroupkey != null"
                  v-model="subgroup.chat"
                  :label="$t('subgroup.createChat')"
                ></v-checkbox>
              </v-col>
              <v-col cols="3" class="">
                <div class="mt-4 text-center">Color</div>
                <div class="d-flex flex-wrap pl-2">
                  <v-btn
                    icon
                    v-for="color in colors"
                    :key="color"
                    @click="subgroup.color = color"
                    :disabled="disabledColors.includes(color)"
                  >
                    <v-icon v-if="subgroup.color == color" size="30" :color="color"
                      >mdi-check-circle</v-icon
                    >
                    <div
                      v-else
                      :style="`width:24px;height:24px;border-radius:13px;background-color:${color};
                      opacity:${disabledColors.includes(color) ? '0.1' : '1'}`"
                    ></div>
                  </v-btn>
                  <v-dialog v-model="colorDialog" width="350">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon v-bind="attrs" v-on="on">
                        <v-icon size="30" color="#6d7278">mdi-plus-circle</v-icon>
                      </v-btn>
                    </template>
                    <v-card>
                      <v-card-title>Add Color</v-card-title>
                      <v-card-text>
                        <v-color-picker
                          v-model="addColor"
                          class="ma-2"
                          show-swatches
                          mode="hexa"
                        ></v-color-picker>
                      </v-card-text>
                      <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn color="primary" text @click="colorDialog = false">cancel</v-btn>
                        <v-btn class="primary" @click="saveColor" :loading="savingColor"
                          >add
                        </v-btn>
                      </v-card-actions>
                    </v-card>
                  </v-dialog>
                </div>
              </v-col>
            </v-row>
            <p v-show="feedback" class="error--text">
              {{ feedback }}
            </p>
          </v-container>
        </v-card-text>
        <v-divider></v-divider>

        <v-card-actions>
          <div class="flex-grow-1"></div>
          <v-btn color="primary" @click="closeDialog" text>{{ $t("common.cancel") }}</v-btn>
          <v-btn color="primary" :loading="dialogLoading" @click="saveSubgroup">{{
            $t("common.save")
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import firestore from "@/firebase/firestore";
import subgroupColors from "@/assets/subgroup_colors.json";

export default {
  name: "SubgroupInfo",
  props: ["subgroupkey", "noActivator", "dialog"],
  data() {
    return {
      colorDialog: false,
      savingColor: false,
      subgroupDialog: false,
      showPicker: false,
      subgroup: {
        color: "",
        chat: false,
      },
      autocomplete: null,
      users: [],
      dialogLoading: false,
      feedback: null,
      formValid: false,
      addColor: "#FF0000",
      rules: {
        required: (value) => !!value || "Required.",

        // eslint-disable-next-line no-confusing-arrow
        nameNotTaken: (value) =>
          // eslint-disable-next-line implicit-arrow-linebreak
          this.$props.subgroupkey
            ? null
            : !this.namesTaken.includes((value || "").toLowerCase()) || "Name Taken",
      },
    };
  },
  created() {
    if (this.dialog) {
      this.subgroupDialog = true;
    }
  },
  computed: {
    tripColors() {
      const { trip } = this.$store.state.trip;
      if (!trip) return [];
      return this.$store.state.trip.trip.colors || [];
    },
    disabledColors() {
      const { subgroups } = this.$store.state.trip;
      return Object.values(subgroups)
        .map((s) => s.color)
        .filter((c) => c);
    },
    colors() {
      return [...subgroupColors, ...this.tripColors];
    },
    allUsers() {
      return this.$store.getters.allUsers.filter((u) => !this.users.includes(u.id));
    },
    namesTaken() {
      return Object.values(this.$store.state.trip.subgroups).map((s) => s.name.toLowerCase());
    },
    subgroupName() {
      const { subgroupkey } = this.$props;

      if (subgroupkey) {
        const subgroup = this.$store.state.trip.subgroups[subgroupkey];
        return subgroup.name;
      }
      return "Subgroup";
    },
  },
  watch: {
    dialog(newVal) {
      if (newVal) {
        this.subgroupDialog = true;
      }
    },
    subgroupkey(newVal) {
      if (newVal && this.$props.dialog === undefined) {
        this.subgroupDialog = true;
      }
    },
    subgroupDialog(newVal) {
      if (!newVal) return;
      const { subgroupkey } = this.$props;
      if (subgroupkey) {
        const subgroup = this.$store.state.trip.subgroups[subgroupkey];
        this.subgroup = {
          name: subgroup.name,
          description: subgroup.description || "",
          chat: subgroup.chat || false,
          color: subgroup.color,
        };
        this.users = Object.keys(subgroup.user_ids);
      }
    },
  },
  methods: {
    userName(id) {
      return this.$store.getters.getUserById(id).name;
    },
    removeUser(userId) {
      this.users.splice(this.users.indexOf(userId), 1);
    },
    selectedUser(data) {
      this.users.push(data);
      this.autocomplete = null;
    },
    async saveSubgroup() {
      if (!this.$refs.form.validate()) {
        return;
      }
      if (!this.subgroup.name) {
        this.feedback = this.$t("common.rules.name");
        return;
      }
      if (!this.subgroup.color) {
        this.feedback = this.$t("common.rules.color");
        return;
      }

      this.feedback = null;
      this.dialogLoading = true;

      const data = this.subgroup;

      const userIds = {};

      this.users.forEach((user) => {
        userIds[user] = true;
      });

      data.user_ids = userIds;

      const tripId = this.$store.getters.currentTripId;
      console.log(data);
      const oldDoc = this.$props.subgroupkey;
      const ref = await firestore.writeDocument(tripId, "subgroups", data, oldDoc);

      if (data.chat) {
        await this.createConversation(tripId, oldDoc || ref.id, {
          users: userIds,
          name: this.subgroup.name,
        });
      }
      this.dialogLoading = false;
      this.closeDialog();
    },
    async saveColor() {
      this.savingColor = true;
      console.log(this.addColor);
      const tripId = this.$store.getters.currentTripId;
      const data = {
        colors: firestore.fieldArrayUnion(this.addColor),
      };
      await firestore.updateTrip(tripId, data);
      this.subgroup.color = this.addColor;
      this.savingColor = false;
      this.colorDialog = false;
    },
    createConversation(tripId, docId, payload) {
      console.log("createConversation");
      console.log(this.$props.subgroupkey);
      if (!this.$props.subgroupkey) {
        console.log("just created");
        // eslint-disable-next-line no-param-reassign
        payload.created = new Date();
        // has to be this way as we want to create a doc with specific id, otherwise it
        // would be updated
        return firestore.tripRef(tripId).collection("chats").doc(docId).set(payload);
      }
      console.log("updating");
      return firestore.writeDocument(tripId, "chats", payload, docId);
    },
    closeDialog() {
      this.subgroupDialog = false;
      this.$emit("closed");
    },
  },
};
</script>
